import {Color} from "@/lib/styles";
import ActionButton from "@/lib/components/ActionButton";
import Page from "@/lib/components/Page";
import {useState} from "react";
import Header from "@/lib/components/Header";
import Nav from "@/lib/components/Nav";
import Message from "@/lib/components/Message";
import {useSession} from "@/lib/auth/auth";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {AccountEditRequest, MyAccount} from "@/lib/types";
import {dataRetryPolicy, fetchBackend, fetchBackendNoContent} from "@/lib/backend";
import ErrorPage from "@/lib/components/ErrorPage";
import SpinnerPage from "@/lib/components/SpinnerPage";
import {TextInput} from "react-native";
import globstyles from "@/lib/globstyles";
import ErrorMessage from "@/lib/components/ErrorMessage";
import {useRouter} from "expo-router";

export default function SettingsPage() {
    const {session, clearSession} = useSession();
    const queryClient = useQueryClient();
    const router = useRouter();

    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    // todo const [timezone, setTimezone] = useState<string>("");
    const [emailBusy, setEmailBusy] = useState(false);
    const [passwordBusy, setPasswordBusy] = useState(false);
    const [saveEmailError, setSaveEmailError] = useState<Error | null>(null);
    const [savePasswordError, setSavePasswordError] = useState<Error | null>(null);

    const {data, isPending, error} = useQuery<MyAccount>({
        queryKey: ['account', session],
        queryFn: () => fetchBackend(`/account`, "GET", session),
        retry: dataRetryPolicy,
    })

    if (error) {
        return <ErrorPage error={error}/>
    } else if (isPending) {
        return <SpinnerPage/>
    }

    async function saveSettings() {
        setEmailBusy(true);
        setSaveEmailError(null);
        setSavePasswordError(null);
        const changes: AccountEditRequest = {
            email: !!email ? email : "",
        };
        fetchBackendNoContent(`/account`, "PATCH", session, changes).then(async () => {
            setEmail("");
            await queryClient.invalidateQueries({queryKey: ['account', session]});
            router.dismiss();
        }).catch((error) => {
            setSaveEmailError(error);
        }).finally(() => {
            setEmailBusy(false);
        });
    }

    async function savePassword() {
        setPasswordBusy(true);
        setSaveEmailError(null);
        setSavePasswordError(null);
        const changes: AccountEditRequest = {
            password: !!password ? password : "",
        };
        fetchBackendNoContent(`/account`, "PATCH", session, changes).then(async () => {
            setPassword("");
            clearSession();
            router.replace(`/u`);
        }).catch((error) => {
            setSavePasswordError(error);
        }).finally(() => {
            setPasswordBusy(false);
        });
    }

    return (
        <Page scrollable={true}>
            <Header title={"E-postadresse"}/>
            {data.email && <Message text={data.email}/>}

            {data.pending_emails.map(email => (
                <Message key={email} text={"Vi har sendt en link til " + email + ".\n\nTrykk på linken for å bekrefte e-postadressen."}/>
            ))}

            <TextInput style={globstyles.formTextInput}
                       placeholder={"Bytt e-postadresse"}
                       placeholderTextColor={Color.FadedText}
                       value={email}
                       onChangeText={(value) => {
                           setEmail(value);
                           setSaveEmailError(null);
                       }}
                       inputMode={"email"}
                       textContentType={"emailAddress"}
                       autoCapitalize={"none"}
                       returnKeyType={"send"}
                       enablesReturnKeyAutomatically={true}
                       focusable={true}
                       keyboardAppearance={"dark"}
                       submitBehavior={"submit"}
                       onSubmitEditing={() => saveSettings()}
            />

            {saveEmailError && <ErrorMessage error={saveEmailError}/>}

            <Nav>
                <ActionButton title={"Send verifikasjonskode"} busy={emailBusy} color={Color.Green} onPress={saveSettings}/>
            </Nav>

            <Header title={"Passord"}/>
            <TextInput style={globstyles.formTextInput}
                       placeholder={"Bytt passord"}
                       placeholderTextColor={Color.FadedText}
                       textAlignVertical={"center"}
                       value={password}
                       onChangeText={(value) => {
                           setPassword(value);
                           setSavePasswordError(null);
                       }}
                       inputMode={"text"}
                       textContentType={"newPassword"}
                       secureTextEntry={true}
                       returnKeyType={"send"}
                       enablesReturnKeyAutomatically={true}
                       focusable={true}
                       keyboardAppearance={"dark"}
                       submitBehavior={"submit"}
                       onSubmitEditing={() => savePassword()}
            />
            <Message text={"Ved å bytte passord blir kontoen din automatisk logget ut fra alle enheter, også den du bruker nå. Bytt passord hvis du mistenker at noen har fått tilgang til kontoen din."}/>

            {savePasswordError && <ErrorMessage error={savePasswordError}/>}

            <Nav>
                <ActionButton title={"Bytt passord"} busy={passwordBusy} color={Color.Green} onPress={savePassword}/>
            </Nav>

            <Header title={"Tidssone"}/>
            {data.email && <Message text={data.timezone}/>}

            {/*<Header title={"Testskjerm"}/>*/}
            {/*<Nav style={{alignItems: "flex-start"}}>*/}
            {/*    <NavButton title={"Åpne testskjerm"} href={`/u/account/debug`}/>*/}
            {/*</Nav>*/}
        </Page>
    );
}